import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SendEmailModal from '../components/SendEmailModal';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { cn } from '../lib/utils';

// Icons (using emoji for consistency with existing code)
const icons = {
  dashboard: '📊',
  templates: '📝',
  activity: '📤',
  monitoring: '🔄',
  compose: '✉️',
  plus: '➕',
  edit: '✏️',
  delete: '🗑️',
  preview: '👁️',
  save: '💾',
  cancel: '❌',
  check: '✅',
  warning: '⚠️',
  play: '▶️',
  stop: '⏹️',
  refresh: '🔄',
  testEmail: '📧',
};

function Communication() {
  const { authenticatedFetch } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({
    templates: { total: 0, active: 0 },
    sentMessages: { total: 0, sent: 0, failed: 0, replied: 0 },
    replies: { total: 0, new: 0, processed: 0 },
    monitoring: { isRunning: false, lastCheck: null }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);

  // Template Editor State
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    category: 'general',
    bodyTemplate: '',
    isHtml: true,
    variables: [],
    active: true
  });
  const [templatePreview, setTemplatePreview] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  // Activity State
  const [messages, setMessages] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityPagination, setActivityPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [activityFilters, setActivityFilters] = useState({
    status: 'all',
    recipientEmail: ''
  });
  const [selectedMessage, setSelectedMessage] = useState(null);

  // Monitoring State
  const [monitoringStatus, setMonitoringStatus] = useState({
    isRunning: false,
    lastCheck: null,
    checkCount: 0,
    intervalMinutes: 5
  });
  const [monitoringAction, setMonitoringAction] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  // Load initial data
  useEffect(() => {
    loadCommunicationData();
  }, []);

  // Load activity when tab changes
  useEffect(() => {
    if (activeTab === 'activity') {
      loadActivityData();
    }
  }, [activeTab, activityPagination.page, activityFilters]);

  const loadCommunicationData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [dashboardResponse, templatesResponse, monitoringResponse] = await Promise.all([
        authenticatedFetch('/api/communication/dashboard'),
        authenticatedFetch('/api/email-templates'),
        authenticatedFetch('/api/email-monitoring/status')
      ]);

      const [dashboardData, templatesData, monitoringData] = await Promise.all([
        dashboardResponse.json(),
        templatesResponse.json(),
        monitoringResponse.json()
      ]);

      if (!dashboardData.success) {
        throw new Error(dashboardData.error);
      }

      const dashboard = dashboardData.data;

      setStats({
        templates: {
          total: templatesData.success ? templatesData.count : 0,
          active: templatesData.success ? templatesData.data.filter(t => t.active).length : 0
        },
        sentMessages: dashboard.sentMessages,
        replies: dashboard.replies,
        monitoring: monitoringData.success ? monitoringData.data : { isRunning: false, lastCheck: null }
      });

      setTemplates(templatesData.success ? templatesData.data : []);
      setMonitoringStatus(monitoringData.success ? monitoringData.data : { isRunning: false, lastCheck: null });

      setRecentActivity([
        ...dashboard.recentActivity.sentMessages.map(msg => ({
          ...msg,
          type: 'sent',
          timestamp: msg.sentAt
        })),
        ...dashboard.recentActivity.replies.map(reply => ({
          ...reply,
          type: 'reply',
          timestamp: reply.receivedAt
        }))
      ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, 10));

    } catch (err) {
      console.error('Error loading communication data:', err);
      setError('Failed to load communication data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadActivityData = async () => {
    try {
      setActivityLoading(true);
      const params = new URLSearchParams({
        page: activityPagination.page.toString(),
        limit: activityPagination.limit.toString()
      });

      if (activityFilters.status && activityFilters.status !== 'all') params.append('status', activityFilters.status);
      if (activityFilters.recipientEmail) params.append('recipientEmail', activityFilters.recipientEmail);

      const response = await authenticatedFetch(`/api/communication/sent-messages?${params}`);
      const data = await response.json();

      if (data.success) {
        setMessages(data.data.messages);
        setActivityPagination(prev => ({
          ...prev,
          total: data.data.pagination.total,
          pages: data.data.pagination.pages
        }));
      }
    } catch (err) {
      console.error('Error loading activity:', err);
    } finally {
      setActivityLoading(false);
    }
  };

  // Template Functions
  const handleCreateTemplate = () => {
    setEditingTemplate('new');
    setTemplateForm({
      name: '',
      subject: '',
      category: 'general',
      bodyTemplate: '',
      isHtml: true,
      variables: [],
      active: true
    });
    setTemplatePreview(null);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template._id);
    setTemplateForm({
      name: template.name,
      subject: template.subject,
      category: template.category,
      bodyTemplate: template.bodyTemplate,
      isHtml: template.isHtml,
      variables: template.variables || [],
      active: template.active
    });
    setTemplatePreview(null);
  };

  const handleCancelEdit = () => {
    setEditingTemplate(null);
    setTemplateForm({
      name: '',
      subject: '',
      category: 'general',
      bodyTemplate: '',
      isHtml: true,
      variables: [],
      active: true
    });
    setTemplatePreview(null);
  };

  const extractVariables = (text) => {
    const regex = /\{\{(\w+)\}\}/g;
    const matches = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (!matches.includes(match[1])) {
        matches.push(match[1]);
      }
    }
    return matches;
  };

  const handleBodyChange = (e) => {
    const bodyTemplate = e.target.value;
    const variableNames = extractVariables(bodyTemplate);
    // Convert to objects that match the backend schema
    const variables = variableNames.map(name => ({
      name,
      description: '',
      required: false,
      defaultValue: ''
    }));
    setTemplateForm(prev => ({ ...prev, bodyTemplate, variables }));
  };

  const handlePreviewTemplate = async () => {
    if (!templateForm.bodyTemplate) {
      alert('Please enter template body first');
      return;
    }

    // Generate preview with sample data
    const sampleData = {
      firstName: 'John',
      lastName: 'Doe',
      email: 'john@example.com',
      tournamentName: 'Sample Tournament',
      teeTime: '8:30 AM',
      date: new Date().toLocaleDateString()
    };

    let preview = templateForm.bodyTemplate;
    templateForm.variables.forEach(variable => {
      const varName = typeof variable === 'object' ? variable.name : variable;
      const regex = new RegExp(`\\{\\{${varName}\\}\\}`, 'g');
      preview = preview.replace(regex, sampleData[varName] || `[${varName}]`);
    });

    setTemplatePreview(preview);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name || !templateForm.subject || !templateForm.bodyTemplate) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setSavingTemplate(true);
      const method = editingTemplate === 'new' ? 'POST' : 'PUT';
      const url = editingTemplate === 'new'
        ? '/api/email-templates'
        : `/api/email-templates/${editingTemplate}`;

      const response = await authenticatedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(templateForm)
      });

      const data = await response.json();

      if (data.success) {
        await loadCommunicationData();
        handleCancelEdit();
        alert(editingTemplate === 'new' ? 'Template created!' : 'Template updated!');
      } else {
        throw new Error(data.error);
      }
    } catch (err) {
      alert('Error saving template: ' + err.message);
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      const response = await authenticatedFetch(`/api/email-templates/${templateId}`, {
        method: 'DELETE'
      });
      const data = await response.json();

      if (data.success) {
        await loadCommunicationData();
        alert('Template deleted');
      } else {
        throw new Error(data.error);
      }
    } catch (err) {
      alert('Error deleting template: ' + err.message);
    }
  };

  const handleSendTestEmail = async () => {
    // Must have a saved template (not 'new')
    if (editingTemplate === 'new') {
      alert('Please save the template first before sending a test email.');
      return;
    }

    const testEmail = window.prompt('Enter email address to send test to:');
    if (!testEmail || !testEmail.includes('@')) {
      if (testEmail !== null) alert('Please enter a valid email address.');
      return;
    }

    try {
      setSendingTestEmail(true);

      // Build sample data for template variables
      const sampleData = {};
      templateForm.variables.forEach(variable => {
        const varName = typeof variable === 'object' ? variable.name : variable;
        if (varName.toLowerCase().includes('name')) sampleData[varName] = 'Test Player';
        else if (varName.toLowerCase().includes('date')) sampleData[varName] = new Date().toLocaleDateString();
        else if (varName.toLowerCase().includes('time')) sampleData[varName] = '8:00 AM';
        else if (varName.toLowerCase().includes('course')) sampleData[varName] = 'Balboa Park Golf Course';
        else if (varName.toLowerCase().includes('event')) sampleData[varName] = 'Test Tournament';
        else sampleData[varName] = `[Sample ${varName}]`;
      });

      const response = await authenticatedFetch(`/api/email-templates/${editingTemplate}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipientEmail: testEmail,
          variables: sampleData
        })
      });

      const data = await response.json();

      if (data.success) {
        alert(`Test email sent successfully to ${testEmail}!`);
      } else {
        throw new Error(data.error || 'Failed to send test email');
      }
    } catch (err) {
      alert('Error sending test email: ' + err.message);
    } finally {
      setSendingTestEmail(false);
    }
  };

  // Monitoring Functions
  const handleStartMonitoring = async () => {
    try {
      setMonitoringAction(true);
      const response = await authenticatedFetch('/api/email-monitoring/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ intervalMinutes: 5 })
      });
      const data = await response.json();

      if (data.success) {
        await loadCommunicationData();
      } else {
        throw new Error(data.error);
      }
    } catch (err) {
      alert('Error starting monitoring: ' + err.message);
    } finally {
      setMonitoringAction(false);
    }
  };

  const handleStopMonitoring = async () => {
    try {
      setMonitoringAction(true);
      const response = await authenticatedFetch('/api/email-monitoring/stop', {
        method: 'POST'
      });
      const data = await response.json();

      if (data.success) {
        await loadCommunicationData();
      } else {
        throw new Error(data.error);
      }
    } catch (err) {
      alert('Error stopping monitoring: ' + err.message);
    } finally {
      setMonitoringAction(false);
    }
  };

  const handleManualCheck = async () => {
    try {
      setMonitoringAction(true);
      const response = await authenticatedFetch('/api/email-monitoring/check', { method: 'POST' });
      const data = await response.json();

      if (data.success) {
        alert('Manual email check completed!');
        await loadCommunicationData();
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      alert('Error during manual check: ' + error.message);
    } finally {
      setMonitoringAction(false);
    }
  };

  const handleEmailSent = (member, template) => {
    alert(`Email sent successfully to ${member.firstName} ${member.lastName}!`);
    loadCommunicationData();
    if (activeTab === 'activity') {
      loadActivityData();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { variant: 'secondary', color: 'bg-yellow-100 text-yellow-800' },
      sent: { variant: 'default', color: 'bg-green-100 text-green-800' },
      failed: { variant: 'destructive', color: 'bg-red-100 text-red-800' },
      replied: { variant: 'outline', color: 'bg-blue-100 text-blue-800' },
      processed: { variant: 'secondary', color: 'bg-gray-100 text-gray-800' }
    };
    const cfg = config[status] || config.pending;

    return (
      <Badge className={cn("text-xs", cfg.color)}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
        <div className="max-w-7xl mx-auto">
          <Skeleton className="h-4 w-32 mb-6" />
          <div className="flex items-center justify-between mb-8">
            <div>
              <Skeleton className="h-8 w-64 mb-2" />
              <Skeleton className="h-4 w-48" />
            </div>
          </div>
          <Skeleton className="h-12 w-full mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="overflow-hidden">
                <CardContent className="p-6">
                  <Skeleton className="h-10 w-10 rounded-full mb-3" />
                  <Skeleton className="h-6 w-16 mb-2" />
                  <Skeleton className="h-4 w-24" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
        <div className="max-w-7xl mx-auto">
          <Link
            to="/dashboard"
            className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-6 transition-colors"
          >
            ← Back to Dashboard
          </Link>

          <Alert variant="destructive">
            <AlertTitle>Communication Error</AlertTitle>
            <AlertDescription className="flex items-center justify-between">
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={loadCommunicationData}>
                Try Again
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Back Navigation */}
        <Link
          to="/dashboard"
          className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-6 transition-colors"
        >
          ← Back to Dashboard
        </Link>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Communication Hub</h1>
            <p className="text-slate-600 mt-1">Manage email templates, campaigns, and monitoring</p>
          </div>
          <Button
            onClick={() => setShowSendEmailModal(true)}
            className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
          >
            {icons.compose} Compose Email
          </Button>
        </div>

        {/* Tab Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border shadow-sm p-1 h-auto flex-wrap">
            <TabsTrigger value="dashboard" className="px-4 py-2">
              {icons.dashboard} Dashboard
            </TabsTrigger>
            <TabsTrigger value="templates" className="px-4 py-2">
              {icons.templates} Templates
            </TabsTrigger>
            <TabsTrigger value="activity" className="px-4 py-2">
              {icons.activity} Activity
            </TabsTrigger>
            <TabsTrigger value="monitoring" className="px-4 py-2">
              {icons.monitoring} Monitoring
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Card className="relative overflow-hidden border-0 shadow-lg">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-pink-600 opacity-90" />
                <CardContent className="relative p-6 text-white">
                  <div className="flex items-center gap-4">
                    <div className="text-3xl">{icons.templates}</div>
                    <div>
                      <div className="text-2xl font-bold">{stats.templates.total}</div>
                      <div className="text-purple-100 text-sm">Email Templates</div>
                      <div className="text-purple-200 text-xs mt-1">{stats.templates.active} active</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden border-0 shadow-lg">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-indigo-600 opacity-90" />
                <CardContent className="relative p-6 text-white">
                  <div className="flex items-center gap-4">
                    <div className="text-3xl">{icons.activity}</div>
                    <div>
                      <div className="text-2xl font-bold">{stats.sentMessages.total}</div>
                      <div className="text-blue-100 text-sm">Messages Sent</div>
                      <div className="text-blue-200 text-xs mt-1">
                        {stats.sentMessages.sent} successful, {stats.sentMessages.failed} failed
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden border-0 shadow-lg">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 to-teal-600 opacity-90" />
                <CardContent className="relative p-6 text-white">
                  <div className="flex items-center gap-4">
                    <div className="text-3xl">📨</div>
                    <div>
                      <div className="text-2xl font-bold">{stats.replies.total}</div>
                      <div className="text-emerald-100 text-sm">Replies Received</div>
                      <div className="text-emerald-200 text-xs mt-1">
                        {stats.replies.new} new, {stats.replies.processed} processed
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden border-0 shadow-lg">
                <div className={cn(
                  "absolute inset-0 opacity-90",
                  stats.monitoring.isRunning
                    ? "bg-gradient-to-br from-green-500 to-emerald-600"
                    : "bg-gradient-to-br from-amber-500 to-orange-600"
                )} />
                <CardContent className="relative p-6 text-white">
                  <div className="flex items-center gap-4">
                    <div className="text-3xl">{stats.monitoring.isRunning ? icons.check : icons.warning}</div>
                    <div>
                      <div className="text-2xl font-bold">{stats.monitoring.isRunning ? 'Active' : 'Stopped'}</div>
                      <div className={cn(
                        "text-sm",
                        stats.monitoring.isRunning ? "text-green-100" : "text-amber-100"
                      )}>Email Monitoring</div>
                      <div className={cn(
                        "text-xs mt-1",
                        stats.monitoring.isRunning ? "text-green-200" : "text-amber-200"
                      )}>
                        {stats.monitoring.lastCheck ?
                          `Last: ${new Date(stats.monitoring.lastCheck).toLocaleTimeString()}` :
                          'Never checked'
                        }
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Activity */}
            <Card className="shadow-sm">
              <CardHeader className="border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    📈 Recent Activity
                  </CardTitle>
                  <Button variant="outline" size="sm" onClick={() => setActiveTab('activity')}>
                    View All
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                {recentActivity.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-5xl mb-4">📊</div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">No Recent Activity</h3>
                    <p className="text-sm text-slate-600">Send an email to see activity here.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recentActivity.slice(0, 5).map((item, index) => (
                      <div
                        key={index}
                        className={cn(
                          "flex items-start gap-4 p-4 rounded-lg border transition-all duration-200 hover:shadow-sm",
                          item.type === 'sent'
                            ? "bg-blue-50 border-blue-200"
                            : "bg-emerald-50 border-emerald-200"
                        )}
                      >
                        <div className={cn(
                          "flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-lg",
                          item.type === 'sent' ? "bg-blue-100" : "bg-emerald-100"
                        )}>
                          {item.type === 'sent' ? '📤' : '📨'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 truncate">
                            {item.type === 'sent' ?
                              `Email sent: ${item.subject}` :
                              `Reply received: ${item.subject}`
                            }
                          </p>
                          <p className="text-sm text-slate-600 mt-1">
                            {item.type === 'sent' ?
                              `To: ${item.recipientEmail}` :
                              `From: ${item.fromEmail}`
                            } • {new Date(item.timestamp).toLocaleString()}
                          </p>
                        </div>
                        <Badge
                          variant={item.status === 'sent' || item.status === 'processed' ? 'success' : 'secondary'}
                          className="shrink-0"
                        >
                          {item.status || 'completed'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates">
            <Card className="shadow-sm">
              <CardHeader className="border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    {icons.templates} Email Templates
                  </CardTitle>
                  <Button onClick={handleCreateTemplate} disabled={editingTemplate !== null}>
                    {icons.plus} Create Template
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                {/* Template Editor */}
                {editingTemplate !== null && (
                  <div className="mb-8 p-6 bg-slate-50 rounded-lg border-2 border-blue-200">
                    <h3 className="text-lg font-semibold mb-4">
                      {editingTemplate === 'new' ? 'Create New Template' : 'Edit Template'}
                    </h3>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Form Side */}
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Template Name *</label>
                          <Input
                            value={templateForm.name}
                            onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="e.g., Tournament Invitation"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-1">Subject Line *</label>
                          <Input
                            value={templateForm.subject}
                            onChange={(e) => setTemplateForm(prev => ({ ...prev, subject: e.target.value }))}
                            placeholder="e.g., You're Invited to {{tournamentName}}"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-1">Category</label>
                            <Select
                              value={templateForm.category}
                              onValueChange={(value) => setTemplateForm(prev => ({ ...prev, category: value }))}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="general">General</SelectItem>
                                <SelectItem value="tournament">Tournament</SelectItem>
                                <SelectItem value="membership">Membership</SelectItem>
                                <SelectItem value="notification">Notification</SelectItem>
                                <SelectItem value="proshop">Proshop</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Format</label>
                            <Select
                              value={templateForm.isHtml ? 'html' : 'text'}
                              onValueChange={(value) => setTemplateForm(prev => ({ ...prev, isHtml: value === 'html' }))}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="html">HTML</SelectItem>
                                <SelectItem value="text">Plain Text</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-1">
                            Email Body *
                            <span className="text-xs text-slate-500 ml-2">
                              Use {'{{variableName}}'} for dynamic content
                            </span>
                          </label>
                          <Textarea
                            value={templateForm.bodyTemplate}
                            onChange={handleBodyChange}
                            placeholder={templateForm.isHtml ?
                              "<h1>Hello {{firstName}}</h1>\n<p>Welcome to {{tournamentName}}!</p>" :
                              "Hello {{firstName}},\n\nWelcome to {{tournamentName}}!"
                            }
                            className="min-h-[200px] font-mono text-sm"
                          />
                        </div>

                        {templateForm.variables.length > 0 && (
                          <div>
                            <label className="block text-sm font-medium mb-2">Detected Variables</label>
                            <div className="flex flex-wrap gap-2">
                              {templateForm.variables.map((v, i) => (
                                <Badge key={i} variant="secondary" className="text-xs">
                                  {`{{${typeof v === 'object' ? v.name : v}}}`}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="active"
                            checked={templateForm.active}
                            onChange={(e) => setTemplateForm(prev => ({ ...prev, active: e.target.checked }))}
                            className="rounded"
                          />
                          <label htmlFor="active" className="text-sm">Active template</label>
                        </div>

                        <div className="flex gap-3 pt-4">
                          <Button onClick={handleSaveTemplate} disabled={savingTemplate}>
                            {savingTemplate ? 'Saving...' : `${icons.save} Save Template`}
                          </Button>
                          <Button variant="outline" onClick={handlePreviewTemplate}>
                            {icons.preview} Preview
                          </Button>
                          <Button
                            variant="outline"
                            onClick={handleSendTestEmail}
                            disabled={editingTemplate === 'new' || sendingTestEmail}
                            title={editingTemplate === 'new' ? 'Save template first' : 'Send test email'}
                          >
                            {sendingTestEmail ? 'Sending...' : `${icons.testEmail} Send Test`}
                          </Button>
                          <Button variant="ghost" onClick={handleCancelEdit}>
                            {icons.cancel} Cancel
                          </Button>
                        </div>
                      </div>

                      {/* Preview Side */}
                      <div>
                        <label className="block text-sm font-medium mb-2">Preview</label>
                        <div className="border rounded-lg bg-white min-h-[300px] p-4">
                          {templatePreview ? (
                            templateForm.isHtml ? (
                              <div dangerouslySetInnerHTML={{ __html: templatePreview }} />
                            ) : (
                              <pre className="whitespace-pre-wrap font-sans text-sm">{templatePreview}</pre>
                            )
                          ) : (
                            <div className="text-center text-slate-400 py-12">
                              <div className="text-4xl mb-2">{icons.preview}</div>
                              <p>Click "Preview" to see how your template will look</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Templates Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {templates.map(template => (
                    <Card key={template._id} className={cn(
                      "hover:shadow-md transition-shadow",
                      editingTemplate === template._id && "ring-2 ring-blue-500"
                    )}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h3 className="font-semibold text-slate-900 truncate">{template.name}</h3>
                          <div className="flex gap-1">
                            <Badge
                              variant="outline"
                              className={cn(
                                "shrink-0 capitalize text-xs",
                                template.category === 'tournament' && "border-purple-300 text-purple-700",
                                template.category === 'membership' && "border-blue-300 text-blue-700",
                                template.category === 'general' && "border-slate-300 text-slate-700",
                                template.category === 'notification' && "border-amber-300 text-amber-700",
                                template.category === 'proshop' && "border-green-300 text-green-700"
                              )}
                            >
                              {template.category}
                            </Badge>
                            {!template.active && (
                              <Badge variant="secondary" className="text-xs">Inactive</Badge>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-slate-600 truncate mb-3">{template.subject}</p>
                        <div className="flex items-center justify-between text-xs text-slate-500 mb-3">
                          <span>{template.variables?.length || 0} variables</span>
                          <Badge variant="secondary" className="text-xs">
                            {template.isHtml ? 'HTML' : 'Text'}
                          </Badge>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="flex-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            onClick={() => handleEditTemplate(template)}
                            disabled={editingTemplate !== null}
                          >
                            {icons.edit} Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteTemplate(template._id)}
                            disabled={editingTemplate !== null}
                          >
                            {icons.delete}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {templates.length === 0 && !editingTemplate && (
                  <div className="text-center py-12">
                    <div className="text-5xl mb-4">{icons.templates}</div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">No Templates Yet</h3>
                    <p className="text-sm text-slate-600 mb-4">Create your first email template to get started.</p>
                    <Button onClick={handleCreateTemplate}>Create Template</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity">
            <Card className="shadow-sm">
              <CardHeader className="border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    {icons.activity} Email Activity
                  </CardTitle>
                  <div className="flex gap-2">
                    <Select
                      value={activityFilters.status}
                      onValueChange={(value) => setActivityFilters(prev => ({ ...prev, status: value }))}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="All Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="sent">Sent</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="failed">Failed</SelectItem>
                        <SelectItem value="replied">Replied</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={loadActivityData}>
                      {icons.refresh} Refresh
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                {/* Activity Stats */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <div className="text-2xl font-bold">{activityPagination.total}</div>
                      <div className="text-sm text-slate-500">Total Messages</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {messages.filter(m => m.status === 'sent').length}
                      </div>
                      <div className="text-sm text-slate-500">Sent</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <div className="text-2xl font-bold text-yellow-600">
                        {messages.filter(m => m.status === 'pending').length}
                      </div>
                      <div className="text-sm text-slate-500">Pending</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <div className="text-2xl font-bold text-red-600">
                        {messages.filter(m => m.status === 'failed').length}
                      </div>
                      <div className="text-sm text-slate-500">Failed</div>
                    </CardContent>
                  </Card>
                </div>

                {/* Messages List */}
                {activityLoading ? (
                  <div className="space-y-4">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="border rounded-lg p-4">
                        <Skeleton className="h-4 w-48 mb-2" />
                        <Skeleton className="h-4 w-64 mb-2" />
                        <Skeleton className="h-3 w-32" />
                      </div>
                    ))}
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <div className="text-5xl mb-4">📭</div>
                    <p>No messages found</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {messages.map((message) => (
                      <div
                        key={message._id}
                        className="border rounded-lg p-4 hover:bg-slate-50 transition-colors cursor-pointer"
                        onClick={() => setSelectedMessage(message)}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium">{message.recipientEmail}</span>
                              {message.recipientName && (
                                <span className="text-sm text-slate-500">({message.recipientName})</span>
                              )}
                            </div>
                            <p className="text-sm font-medium mb-1">{message.subject}</p>
                            <div className="flex items-center gap-3 text-xs text-slate-500">
                              <span>{formatDate(message.sentAt)}</span>
                              {message.templateName && (
                                <Badge variant="outline" className="text-xs">{message.templateName}</Badge>
                              )}
                            </div>
                          </div>
                          {getStatusBadge(message.status)}
                        </div>
                        {message.errorMessage && (
                          <div className="mt-2 p-2 bg-red-50 rounded text-sm text-red-600">
                            {icons.warning} {message.errorMessage}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {activityPagination.pages > 1 && (
                  <div className="flex items-center justify-between mt-6 pt-4 border-t">
                    <span className="text-sm text-slate-500">
                      Page {activityPagination.page} of {activityPagination.pages}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setActivityPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                        disabled={activityPagination.page <= 1}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setActivityPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                        disabled={activityPagination.page >= activityPagination.pages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Monitoring Tab */}
          <TabsContent value="monitoring">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Status Card */}
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    {icons.monitoring} Monitoring Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={cn(
                    "p-6 rounded-lg mb-6",
                    stats.monitoring.isRunning ? "bg-green-50 border-2 border-green-200" : "bg-amber-50 border-2 border-amber-200"
                  )}>
                    <div className="flex items-center gap-4">
                      <div className={cn(
                        "w-16 h-16 rounded-full flex items-center justify-center text-3xl",
                        stats.monitoring.isRunning ? "bg-green-100" : "bg-amber-100"
                      )}>
                        {stats.monitoring.isRunning ? icons.check : icons.stop}
                      </div>
                      <div>
                        <h3 className={cn(
                          "text-xl font-bold",
                          stats.monitoring.isRunning ? "text-green-700" : "text-amber-700"
                        )}>
                          {stats.monitoring.isRunning ? 'Monitoring Active' : 'Monitoring Stopped'}
                        </h3>
                        <p className="text-sm text-slate-600 mt-1">
                          {stats.monitoring.isRunning
                            ? 'Checking for new email replies automatically'
                            : 'Email replies are not being monitored'
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Last Check:</span>
                      <span className="font-medium">{formatDate(stats.monitoring.lastCheck)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Check Interval:</span>
                      <span className="font-medium">5 minutes</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">New Replies:</span>
                      <span className="font-medium">{stats.replies.new}</span>
                    </div>
                  </div>

                  <div className="flex gap-3 mt-6">
                    {stats.monitoring.isRunning ? (
                      <Button
                        variant="outline"
                        onClick={handleStopMonitoring}
                        disabled={monitoringAction}
                        className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                      >
                        {icons.stop} Stop Monitoring
                      </Button>
                    ) : (
                      <Button
                        onClick={handleStartMonitoring}
                        disabled={monitoringAction}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                      >
                        {icons.play} Start Monitoring
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={handleManualCheck}
                      disabled={monitoringAction}
                    >
                      {icons.refresh} Check Now
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Reply Stats Card */}
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    📨 Reply Statistics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <div className="text-3xl font-bold text-blue-600">{stats.replies.total}</div>
                      <div className="text-sm text-slate-600">Total Replies</div>
                    </div>
                    <div className="p-4 bg-emerald-50 rounded-lg text-center">
                      <div className="text-3xl font-bold text-emerald-600">{stats.replies.new}</div>
                      <div className="text-sm text-slate-600">New Replies</div>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg text-center">
                      <div className="text-3xl font-bold text-purple-600">{stats.replies.processed}</div>
                      <div className="text-sm text-slate-600">Processed</div>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-lg text-center">
                      <div className="text-3xl font-bold text-amber-600">
                        {stats.sentMessages.replied || 0}
                      </div>
                      <div className="text-sm text-slate-600">With Replies</div>
                    </div>
                  </div>

                  <Alert>
                    <AlertTitle>How Monitoring Works</AlertTitle>
                    <AlertDescription className="text-sm text-slate-600 mt-2">
                      <ul className="list-disc list-inside space-y-1">
                        <li>Automatically checks Gmail for new replies</li>
                        <li>Matches replies to sent messages by thread ID</li>
                        <li>Updates message status and stores reply content</li>
                        <li>Runs every 5 minutes when active</li>
                      </ul>
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Send Email Modal */}
      <SendEmailModal
        isOpen={showSendEmailModal}
        onClose={() => setShowSendEmailModal(false)}
        onEmailSent={handleEmailSent}
      />

      {/* Message Detail Modal */}
      {selectedMessage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Email Details</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedMessage(null)}
                >
                  ✕
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-500">To</label>
                    <p>{selectedMessage.recipientEmail}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-500">Status</label>
                    <div className="mt-1">{getStatusBadge(selectedMessage.status)}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-500">Sent From</label>
                    <p>{selectedMessage.sentFrom || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-500">Sent At</label>
                    <p>{formatDate(selectedMessage.sentAt)}</p>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-500">Subject</label>
                  <p className="font-medium">{selectedMessage.subject}</p>
                </div>

                {selectedMessage.templateName && (
                  <div>
                    <label className="text-sm font-medium text-slate-500">Template</label>
                    <p>{selectedMessage.templateName}</p>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-slate-500">Email Body</label>
                  <div
                    className="mt-2 border rounded-lg p-4 bg-white prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: selectedMessage.bodyHtml || selectedMessage.body || '' }}
                  />
                </div>

                {selectedMessage.errorMessage && (
                  <Alert variant="destructive">
                    <AlertDescription>{selectedMessage.errorMessage}</AlertDescription>
                  </Alert>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default Communication;
